
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HelpCircle, Phone, Clock, Shield, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const GeneralRoofingFAQPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Roofing FAQ: Common Questions About Roof Repair & Replacement | Carolina Trophy Roofs</title>
        <meta name="description" content="Get answers to the most common roofing questions about repair, replacement, materials, and costs. Expert advice from Carolina Trophy Roofs in North Carolina." />
        <meta name="keywords" content="roofing FAQ, roof repair questions, roof replacement FAQ, roofing costs, North Carolina roofing" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/resources/general-roofing-faq" />
      </Helmet>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link to="/resources" className="text-primary hover:underline">Resources</Link>
            <span className="mx-2">→</span>
            <span className="text-muted-foreground">General Roofing FAQ</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <HelpCircle className="mr-2 h-4 w-4" />
              Frequently Asked Questions
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Roofing FAQ: Your Most Common Questions Answered
            </h1>
            <p className="text-xl text-gray-600">
              Expert answers to help you make informed decisions about your roof
            </p>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-8">
            {/* General Questions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">General Roofing Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">How long does a typical roof last?</h3>
                  <p className="text-gray-700 mb-3">
                    Roof lifespan varies significantly by material type. According to the National Association of Home Builders, 
                    asphalt shingles typically last 15-30 years, while metal roofs can last 40-70 years. Clay tiles and slate 
                    can last over 100 years with proper maintenance.
                  </p>
                  <p className="text-gray-700">
                    In North Carolina's climate, factors like hurricane-force winds, hail, and humidity can affect these timelines. 
                    Regular inspections and maintenance can help maximize your roof's lifespan.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">How often should I have my roof inspected?</h3>
                  <p className="text-gray-700 mb-3">
                    The Federal Emergency Management Agency (FEMA) recommends having your roof professionally inspected 
                    at least twice per year - once in spring and once in fall. Additionally, inspections should be 
                    conducted after major weather events like hurricanes, hailstorms, or high winds.
                  </p>
                  <p className="text-gray-700">
                    For homeowners in Eastern North Carolina, where severe weather is common, more frequent inspections 
                    may be beneficial, especially during storm season (June through November).
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What are the signs that I need a new roof?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the National Roofing Contractors Association (NRCA), key warning signs include:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Shingles that are curling, cracking, or missing</li>
                    <li>Granules collecting in gutters</li>
                    <li>Dark streaks or stains on the roof</li>
                    <li>Sagging roof deck</li>
                    <li>Daylight visible through roof boards</li>
                    <li>Water stains on interior ceilings or walls</li>
                  </ul>
                  <p className="text-gray-700">
                    If your roof is over 20 years old and showing multiple signs of wear, replacement may be more 
                    cost-effective than continued repairs.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Can I repair my roof myself?</h3>
                  <p className="text-gray-700 mb-3">
                    While minor repairs like replacing a few shingles might seem manageable, the Occupational Safety 
                    and Health Administration (OSHA) reports that falls from roofs account for over 34% of construction 
                    fatalities. Professional roofers have specialized safety equipment and training.
                  </p>
                  <p className="text-gray-700">
                    Additionally, improper repairs can void manufacturer warranties and create larger problems. Most 
                    insurance companies also prefer professional documentation for claims.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Weather-Related Questions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <AlertTriangle className="mr-2 h-6 w-6" />
                  Weather & Storm Damage Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">How do I know if my roof was damaged by a storm?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Insurance Information Institute, storm damage isn't always immediately visible. 
                    Signs to look for include:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Missing or damaged shingles</li>
                    <li>Dents in gutters, vents, or flashing</li>
                    <li>Granule loss (visible as dark spots on shingles)</li>
                    <li>Cracked or exposed roof cement</li>
                    <li>Damaged chimney caps or covers</li>
                  </ul>
                  <p className="text-gray-700">
                    Even minor hail can cause "bruising" that isn't immediately visible but compromises the shingle's 
                    protective granule layer, leading to premature aging.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What wind speeds can damage a roof?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the National Weather Service, roof damage can occur at surprisingly low wind speeds:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>39+ mph: Loose shingles may lift</li>
                    <li>58+ mph: Significant shingle damage possible</li>
                    <li>74+ mph: Hurricane-force winds can cause severe damage</li>
                  </ul>
                  <p className="text-gray-700">
                    Older roofs or those with existing damage are more vulnerable. Proper installation and regular 
                    maintenance improve wind resistance significantly.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">How quickly should I address storm damage?</h3>
                  <p className="text-gray-700 mb-3">
                    The National Association of Insurance Commissioners recommends reporting storm damage as soon as 
                    possible after discovery. Most insurance policies require "prompt" notification, though specific 
                    timeframes vary.
                  </p>
                  <p className="text-gray-700">
                    From a practical standpoint, even small leaks can quickly lead to mold, structural damage, and 
                    higher repair costs. Temporary repairs should be made immediately to prevent further damage.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Maintenance Questions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Shield className="mr-2 h-6 w-6" />
                  Maintenance & Prevention Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">How can I extend my roof's lifespan?</h3>
                  <p className="text-gray-700 mb-3">
                    According to research from the Cool Roof Rating Council and Energy Star, proper maintenance can 
                    extend roof life by 30% or more:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Regular professional inspections</li>
                    <li>Prompt repair of minor issues</li>
                    <li>Keeping gutters clean and functional</li>
                    <li>Ensuring proper attic ventilation</li>
                    <li>Removing debris and overhanging branches</li>
                    <li>Addressing ice dam formation</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Should I clean my roof?</h3>
                  <p className="text-gray-700 mb-3">
                    The Asphalt Roofing Manufacturers Association recommends gentle cleaning methods only. Pressure 
                    washing can damage shingles and void warranties. For algae or moss removal, use zinc or copper 
                    strips or gentle cleaning solutions.
                  </p>
                  <p className="text-gray-700">
                    In North Carolina's humid climate, algae growth is common and primarily aesthetic. However, 
                    moss can retain moisture and cause damage if left untreated.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What about attic ventilation?</h3>
                  <p className="text-gray-700 mb-3">
                    The Department of Energy emphasizes that proper attic ventilation is crucial for roof longevity. 
                    Benefits include:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Reduced energy costs (up to 15% savings)</li>
                    <li>Prevention of ice dams</li>
                    <li>Moisture control to prevent mold</li>
                    <li>Extended shingle life</li>
                  </ul>
                  <p className="text-gray-700">
                    The general rule is 1 square foot of ventilation per 150 square feet of attic space, with 
                    balanced intake and exhaust ventilation.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Situations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Clock className="mr-2 h-6 w-6" />
                  Emergency Situations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">What should I do if my roof is leaking during a storm?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the American Red Cross emergency preparedness guidelines:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-gray-700 mb-3">
                    <li>Ensure personal safety - avoid electrical hazards</li>
                    <li>Place containers to catch water</li>
                    <li>Move belongings away from the leak area</li>
                    <li>Document the damage with photos</li>
                    <li>Contact your insurance company</li>
                    <li>Make temporary repairs only if safe to do so</li>
                  </ol>
                  <p className="text-gray-700">
                    Never attempt roof repairs during a storm. Professional emergency services can provide temporary 
                    protection until permanent repairs can be made safely.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">How much does emergency roof repair cost?</h3>
                  <p className="text-gray-700 mb-3">
                    According to HomeAdvisor's cost data, emergency roof repairs typically cost 2-3 times more than 
                    standard repairs due to urgency and potential after-hours service. Typical emergency repair costs range:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Temporary tarping: $200-$1,000</li>
                    <li>Emergency leak repair: $300-$1,500</li>
                    <li>Storm damage patches: $500-$2,000</li>
                  </ul>
                  <p className="text-gray-700">
                    However, emergency repairs prevent much costlier secondary damage from water intrusion, making 
                    them a worthwhile investment.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Authoritative Sources */}
          <Card className="mt-12">
            <CardHeader>
              <CardTitle>Authoritative Sources & References</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium mb-2">Government & Safety Organizations:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.fema.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Federal Emergency Management Agency (FEMA)
                      </a>
                    </li>
                    <li>
                      <a href="https://www.energy.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        U.S. Department of Energy
                      </a>
                    </li>
                    <li>
                      <a href="https://www.osha.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Occupational Safety and Health Administration (OSHA)
                      </a>
                    </li>
                    <li>
                      <a href="https://www.weather.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        National Weather Service
                      </a>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Industry Organizations:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.nrca.net/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        National Roofing Contractors Association (NRCA)
                      </a>
                    </li>
                    <li>
                      <a href="https://www.iii.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Insurance Information Institute
                      </a>
                    </li>
                    <li>
                      <a href="https://www.nahb.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        National Association of Home Builders
                      </a>
                    </li>
                    <li>
                      <a href="https://www.homeadvisor.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        HomeAdvisor
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Our roofing experts are ready to provide personalized answers for your specific situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <a href="tel:252-371-2266">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (252) 371-2266
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Get Free Consultation</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default GeneralRoofingFAQPage;
